import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { PrimerPaymentFormV2 } from 'components/PrimerPaymentForm'
import { Spinner } from 'components/Spinner'

import { startFetching } from 'root-redux/actions/common'
import { selectActionList, selectScreenName } from 'root-redux/selects/common'

import { useABTest } from 'hooks/useABTest'
import { useAmplitudeInitialization } from 'hooks/useAmplitudeInitialization'
import { useScrollToTop } from 'hooks/useScrollToTop'

import { createProductId } from 'helpers/createProductId'

import { CardPaymentFormSeparateCheckout } from 'modules/purchase/components/CardPaymentFormSeparateCheckout'
import { CheckoutPurchaseDetails } from 'modules/purchase/components/CheckoutPurchaseDetails'
import { PrimerPaymentWaitingModal } from 'modules/purchase/components/PaymentWaitingModal'
import { PurchaseTimer } from 'modules/purchase/components/PurchaseTimer'
import { StripeSeparatePaymentRequestButton } from 'modules/purchase/components/StripeSeparatePaymentRequestButton'
import { PaymentMethod, TEN_MINUTES } from 'modules/purchase/constants'
import { usePurchaseStore, useTimerForTarget } from 'modules/purchase/hooks'
import { useCancelPrimerDiscount } from 'modules/purchase/hooks/useCancelPrimerDiscount'
import {
  CHECK_3D_SECURE,
  CHECK_PAYMENT_REQUEST_BUTTON,
  CHECK_PAYPAL_REQUEST_BUTTON,
  PURCHASE,
  UPDATE_PRIMER_CLIENT_SESSION,
} from 'modules/purchase/redux/actions/common'
import {
  select3DSecureIframeUrl,
  selectSubscription,
} from 'modules/purchase/redux/selects/common'

import { eventLogger } from 'services/eventLogger.service'

import SecuritySystems from 'assets/images/security-systems.png'

import { Cohort, ScreenName } from 'root-constants'

import { StyledCheckoutSeparateMethods as S } from './CheckoutSeparateMethods.styles'
import {
  PAYMENT_METHODS_IMAGES,
  SUPPORTED_ALTERNATIVE_PAYMENT_METHODS,
} from './constants'

type TCheckoutExtended = {
  handleCloseCheckout: () => void
  isPrimerFormActive?: boolean
}

export const CheckoutSeparateMethodsMix: React.FC<TCheckoutExtended> = ({
  handleCloseCheckout,
  isPrimerFormActive,
}: TCheckoutExtended) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const selectedSubscription = useSelector(selectSubscription)
  const fetchingActionsList = useSelector(selectActionList)
  const threeDSecureIframeUrl = useSelector(select3DSecureIframeUrl)
  const screenName = useSelector(selectScreenName)
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null)
  const [alternativePaymentMethods, setAlternativePaymentMethods] = useState<
    PaymentMethod[]
  >([])
  const [isPaymentWaitingShown, setIsPaymentWaitingShown] =
    useState<boolean>(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useScrollToTop()

  const timerElementRef = useRef<HTMLSpanElement | null>(null)

  const {
    periodName,
    periodQuantity,
    trialPrice,
    price,
    cohort,
    stripeAccountId,
    stripeAccountName,
  } = usePurchaseStore()
  const { cancelDiscount } = useCancelPrimerDiscount()

  const { isHolidayActive } = useABTest()
  useTimerForTarget(timerElementRef, TEN_MINUTES.IN_SECONDS)

  const isCheckoutReady = useMemo(
    () =>
      !fetchingActionsList.includes(CHECK_PAYMENT_REQUEST_BUTTON) &&
      !fetchingActionsList.includes(CHECK_PAYPAL_REQUEST_BUTTON),
    [fetchingActionsList],
  )

  const isPurchaseInProcess = useMemo(
    () =>
      fetchingActionsList.includes(PURCHASE) ||
      fetchingActionsList.includes(CHECK_3D_SECURE) ||
      fetchingActionsList.includes(UPDATE_PRIMER_CLIENT_SESSION),
    [fetchingActionsList],
  )

  const isCardSelected = useMemo(
    () => selectedPaymentMethod === PaymentMethod.CREDIT_CARD,
    [selectedPaymentMethod],
  )

  const productId = useMemo(
    () =>
      createProductId({
        periodName,
        periodQuantity,
        price: trialPrice || price,
      }),
    [periodName, periodQuantity, trialPrice, price],
  )

  useLayoutEffect(() => {
    if (isCheckoutReady) {
      const selectedMethod = SUPPORTED_ALTERNATIVE_PAYMENT_METHODS.find(
        (method) => alternativePaymentMethods.includes(method),
      )
      const currentPaymentMethod = selectedMethod || PaymentMethod.CREDIT_CARD
      setSelectedPaymentMethod(currentPaymentMethod)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        eventLogger.logPaymentMethodSelected(currentPaymentMethod)
      }, 300)
    }
  }, [alternativePaymentMethods, isCheckoutReady])

  useAmplitudeInitialization(cohort as Cohort, ScreenName.CHECKOUT)

  useEffect(() => {
    if (productId) {
      eventLogger.logPurchaseShown({
        productId,
        screenName,
        stripeAccountId,
        stripeAccountName,
      })
    }
  }, [productId, screenName, stripeAccountId, stripeAccountName])

  useEffect(() => {
    dispatch(startFetching(CHECK_PAYPAL_REQUEST_BUTTON))
    dispatch(startFetching(CHECK_PAYMENT_REQUEST_BUTTON))
  }, [dispatch])

  useEffect(() => {
    if (isPurchaseInProcess) {
      setIsPaymentWaitingShown(true)
    }
  }, [isPurchaseInProcess])

  const setPaymentMethod = useCallback(
    (paymentMethodFromUser: PaymentMethod) => {
      if (selectedPaymentMethod !== paymentMethodFromUser) {
        setSelectedPaymentMethod(paymentMethodFromUser)
        eventLogger.logPaymentMethodSelected(paymentMethodFromUser)
      }
    },
    [selectedPaymentMethod],
  )

  if (threeDSecureIframeUrl) {
    return <S.ThreeDSecureIframe title="3DSecure" src={threeDSecureIframeUrl} />
  }

  return (
    selectedSubscription && (
      <S.Root>
        {!isCheckoutReady && <Spinner />}
        <S.ContentContainer isVisible={isCheckoutReady}>
          <S.PaymentMethodsTitle>
            {alternativePaymentMethods.length
              ? t('checkoutExtended.selectMethods')
              : t('checkoutExtended.checkoutTitle')}
          </S.PaymentMethodsTitle>
          {isHolidayActive && <PurchaseTimer timerRef={timerElementRef} />}

          <S.CloseButton
            data-testid="close-btn"
            onClick={handleCloseCheckout}
          />
          {!!alternativePaymentMethods.length && (
            <S.PaymentMethodsWrapperV2>
              <S.PaymentMethod
                isSelected={isCardSelected}
                onClick={() => setPaymentMethod(PaymentMethod.CREDIT_CARD)}
              >
                <S.PaymentMethodText>
                  {t('checkoutExtended.creditCard')}
                </S.PaymentMethodText>
                <S.PaymentMethodImage
                  isSelected={isCardSelected}
                  src={PAYMENT_METHODS_IMAGES[PaymentMethod.CREDIT_CARD]}
                  alt="credit-card-img"
                />
              </S.PaymentMethod>
              {alternativePaymentMethods.includes(PaymentMethod.PAYPAL) && (
                <S.PaymentMethod
                  isSelected={selectedPaymentMethod === PaymentMethod.PAYPAL}
                  onClick={() => {
                    setPaymentMethod(PaymentMethod.PAYPAL)
                    cancelDiscount()
                  }}
                >
                  <S.AlternativePaymentMethodImage
                    isSelected={selectedPaymentMethod === PaymentMethod.PAYPAL}
                    src={PAYMENT_METHODS_IMAGES[PaymentMethod.PAYPAL]}
                    alt=""
                  />
                </S.PaymentMethod>
              )}
              {alternativePaymentMethods.includes(PaymentMethod.GOOGLE_PAY) && (
                <S.PaymentMethod
                  isSelected={
                    selectedPaymentMethod === PaymentMethod.GOOGLE_PAY
                  }
                  onClick={() => setPaymentMethod(PaymentMethod.GOOGLE_PAY)}
                >
                  <S.AlternativePaymentMethodImage
                    isSelected={
                      selectedPaymentMethod === PaymentMethod.GOOGLE_PAY
                    }
                    src={PAYMENT_METHODS_IMAGES[PaymentMethod.GOOGLE_PAY]}
                    alt=""
                  />
                </S.PaymentMethod>
              )}
              {alternativePaymentMethods.includes(PaymentMethod.APPLE_PAY) && (
                <S.PaymentMethod
                  isSelected={selectedPaymentMethod === PaymentMethod.APPLE_PAY}
                  onClick={() => setPaymentMethod(PaymentMethod.APPLE_PAY)}
                >
                  <S.AlternativePaymentMethodImage
                    isSelected={
                      selectedPaymentMethod === PaymentMethod.APPLE_PAY
                    }
                    src={PAYMENT_METHODS_IMAGES[PaymentMethod.APPLE_PAY]}
                    alt=""
                  />
                </S.PaymentMethod>
              )}
            </S.PaymentMethodsWrapperV2>
          )}

          <CheckoutPurchaseDetails />

          {!isPrimerFormActive && (
            <S.StripePaymentContainer
              isVisible={selectedPaymentMethod === PaymentMethod.CREDIT_CARD}
            >
              <CardPaymentFormSeparateCheckout />
            </S.StripePaymentContainer>
          )}

          <PrimerPaymentFormV2
            selectedPaymentMethod={
              selectedPaymentMethod || PaymentMethod.CREDIT_CARD
            }
            setAlternativePaymentMethodsCallback={setAlternativePaymentMethods}
            isPrimerFormActive={isPrimerFormActive}
          />
          <S.StripePaymentContainer
            isVisible={
              selectedPaymentMethod === PaymentMethod.GOOGLE_PAY ||
              selectedPaymentMethod === PaymentMethod.APPLE_PAY
            }
          >
            <StripeSeparatePaymentRequestButton
              key={selectedSubscription.id}
              setAlternativePaymentMethodsCallback={
                setAlternativePaymentMethods
              }
            />
          </S.StripePaymentContainer>

          <S.PaymentsSystemImage src={SecuritySystems} alt="security-systems" />
          <S.PaymentsSystemText>{t`purchase2.checkout.moneyBackGuarantee`}</S.PaymentsSystemText>
        </S.ContentContainer>

        <PrimerPaymentWaitingModal
          isPaymentWaitingShown={isPaymentWaitingShown}
          setIsPaymentWaitingShown={setIsPaymentWaitingShown}
          buttonText={t`purchase1.paymentWaiting.proceedToPlan`}
        />
      </S.Root>
    )
  )
}

export default CheckoutSeparateMethodsMix
